import { Suspense, lazy, useEffect, useRef } from 'react'
import { useDevStore } from '@/store/devStore'

// The playground's editor: Monaco, bundled, loaded lazily (MonacoEditor.tsx). Under vitest (no layout, no canvas)
// and while Monaco loads, a plain textarea with the same props stands in, so every test and the first paint work.
const Monaco = lazy(() => import('./MonacoEditor'))

export interface EditorMarker {
  line: number
  text: string
  level: 'error' | 'warning'
}

export interface CodeEditorProps {
  path: string
  value: string
  readOnly: boolean
  onChange: (value: string) => void
  /** Ctrl+S / Cmd+S */
  onSave?: () => void
  /** Ctrl+Enter / Cmd+Enter */
  onRun?: () => void
  /** the caret moved (for the status bar) */
  onCursor?: (line: number, column: number) => void
  /** the editor's own type and syntax findings for this file (Monaco only) */
  onMarkers?: (markers: EditorMarker[]) => void
  /** go to this line; `n` changes on every request so the same line can be asked twice */
  jump?: { line: number; n: number }
  /** other files the editor should know (shared helpers), so their functions are not "unknown names" */
  context?: { path: string; content: string }[]
  dark?: boolean
  height?: number | string
}

export function PlainEditor({ path, value, readOnly, onChange, onSave, onRun, onCursor, jump, height = 460 }: CodeEditorProps) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const report = (el: HTMLTextAreaElement) => {
    const before = el.value.slice(0, el.selectionStart)
    const lines = before.split('\n')
    onCursor?.(lines.length, lines.at(-1)!.length + 1)
  }
  useEffect(() => {
    const el = ref.current
    if (!el || !jump) return
    const at = value.split('\n').slice(0, jump.line - 1).join('\n').length + (jump.line > 1 ? 1 : 0)
    el.focus()
    el.setSelectionRange(at, at)
    report(el)
  }, [jump?.n]) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <textarea
      ref={ref}
      aria-label={`Code of ${path}`}
      className="w-full resize-none bg-white p-3 font-mono text-xs text-slate-800 outline-none dark:bg-slate-950 dark:text-slate-200"
      style={{ height }}
      value={value}
      readOnly={readOnly}
      onChange={(e) => onChange(e.target.value)}
      onKeyUp={(e) => report(e.currentTarget)}
      onClick={(e) => report(e.currentTarget)}
      onKeyDown={(e) => {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
          e.preventDefault()
          onSave?.()
        }
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
          e.preventDefault()
          onRun?.()
        }
      }}
      spellCheck={false}
    />
  )
}

export function CodeEditor(props: CodeEditorProps) {
  const scheme = useDevStore((s) => s.colourScheme)
  if (import.meta.env.MODE === 'test') return <PlainEditor {...props} />
  return (
    <Suspense fallback={<PlainEditor {...props} />}>
      <Monaco {...props} dark={scheme === 'dark'} />
    </Suspense>
  )
}
